import type { JSX } from "react";
import { StyleSheet, Text, View } from "react-native";
import Svg, { Circle } from "react-native-svg";

import { fontFamily } from "@passfirst/ui";

import { useColors } from "./theme";

// Circular readiness ring — the diagnostic + dashboard hero per the
// App Prototype reference. Two concentric circles: a muted track
// (background) and a stroke arc that fills based on `pct`. The arc
// uses stroke-dasharray + stroke-dashoffset to render the partial.
// Center text shows the percentage with a small caption below.
//
// Two sizes ship today:
//   "lg" (168px) → practice.tsx EXHAUSTED diagnostic
//   "sm" (76px)  → home.tsx greeting card

interface Props {
  pct: number;
  // sm 76 (mobile home) · md 96 (web home) · lg 168 (mobile diagnostic)
  // · xl 180 (web diagnostic). Per design_handoff_2 §3/§5.
  size?: "lg" | "sm" | "md" | "xl";
  /** Stroke color for the filled arc. Falls back to navy. */
  trackColor?: string;
  arcColor?: string;
  /** Display-only override for the center number (e.g. "64%" or "5/10"). */
  centerText?: string;
  /** Small caption rendered under the center number (lg only). */
  caption?: string;
  /** Text color for the center number. White on navy in `sm` use. */
  textColor?: string;
  /** Accessible label for the progressbar. Defaults to "Exam readiness". */
  accessibilityLabel?: string;
}

export function ReadinessRing({
  pct,
  size = "lg",
  trackColor,
  arcColor,
  centerText,
  caption,
  textColor,
  accessibilityLabel = "Exam readiness",
}: Props): JSX.Element {
  const colors = useColors();
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));

  const dims = DIMS[size];
  const c = 2 * Math.PI * dims.r;
  const offset = c * (1 - clamped / 100);

  // Default arc color: amber when the diagnostic is mid-range,
  // green at ready, brick when very low. Caller can override.
  const fillColor =
    arcColor ??
    (clamped >= 80
      ? colors.success
      : clamped >= 50
        ? colors.caution
        : colors.error);

  const resolvedTrack = trackColor ?? colors.surfaceMuted;
  const numColor = textColor ?? colors.heading;

  return (
    <View
      style={[styles.wrap, { width: dims.box, height: dims.box }]}
      accessibilityRole="progressbar"
      accessibilityLabel={accessibilityLabel}
      accessibilityValue={{ min: 0, max: 100, now: clamped }}
    >
      <Svg
        width={dims.box}
        height={dims.box}
        viewBox={`0 0 ${dims.box} ${dims.box}`}
        // rotate(-90) makes the arc start at 12 o'clock instead of 3 o'clock
        style={{ transform: [{ rotate: "-90deg" }] }}
      >
        <Circle
          cx={dims.box / 2}
          cy={dims.box / 2}
          r={dims.r}
          stroke={resolvedTrack}
          strokeWidth={dims.stroke}
          fill="none"
        />
        <Circle
          cx={dims.box / 2}
          cy={dims.box / 2}
          r={dims.r}
          stroke={fillColor}
          strokeWidth={dims.stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${c}`}
          strokeDashoffset={`${offset}`}
        />
      </Svg>
      <View
        style={[StyleSheet.absoluteFill, styles.center]}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        aria-hidden
      >
        <Text
          style={[
            size === "lg" || size === "xl" ? styles.numLg : styles.numSm,
            { color: numColor },
          ]}
        >
          {centerText ?? `${clamped}%`}
        </Text>
        {(size === "lg" || size === "xl") && caption ? (
          <Text style={[styles.captionLg, { color: numColor, opacity: 0.6 }]}>
            {caption}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

const DIMS = {
  sm: { box: 76, r: 32, stroke: 5 },
  md: { box: 96, r: 41, stroke: 7 },
  lg: { box: 168, r: 72, stroke: 14 },
  xl: { box: 180, r: 78, stroke: 14 },
} as const;

const styles = StyleSheet.create({
  wrap: { alignItems: "center", justifyContent: "center" },
  center: { alignItems: "center", justifyContent: "center" },
  numLg: {
    fontFamily: fontFamily.extraBold,
    fontSize: 40,
    letterSpacing: -0.5,
  },
  numSm: {
    fontFamily: fontFamily.extraBold,
    fontSize: 18,
  },
  captionLg: {
    fontFamily: fontFamily.semiBold,
    fontSize: 12,
    marginTop: 2,
  },
});
